import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import CreditsEngineService, { toNumber } from '../../../../services/CreditsEngineService';
import HelpIcon from '../../../../components/help/HelpIcon';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

// Espejo de `OperationType.ALL` en whisperbi-credits-engine/src/credits-engine/models.py.
// operation_type es la PK de OperationPricingTable -- enum cerrado, sin crear/borrar filas.
const OPERATION_TYPES = [
  'CHAT', 'NL2SQL_SIMPLE', 'NL2SQL_COMPLEX', 'METADATA_SEARCH', 'SQL_GENERATION',
  'SQL_VALIDATION', 'DATA_ANALYSIS', 'MULTI_AGENT_ANALYSIS', 'REPORT_GENERATION',
  'FORECAST', 'DEEP_ANALYSIS', 'DASHBOARD_REFRESH', 'CHART_GENERATION', 'OTHER'
];

// Espejo de `DEFAULT_TOKEN_ESTIMATES` en whisperbi-credits-engine/src/credits-engine/pricing_resolver.py:
// tokens típicos (entrada, salida) con los que se estima la reserva previa
// según el modelo que va a responder, cuando la fila no los define.
export const TOKEN_ESTIMATES_DEFAULT = {
  CHAT: [33000, 1500],
  DEEP_ANALYSIS: [200000, 8000],
  REPORT_GENERATION: [20000, 6000],
  CHART_GENERATION: [4000, 1000]
};

const formatTokens = (value) => Number(value).toLocaleString('es-MX');

// Texto de la columna "Tokens estimados": los de la fila, o el default del
// código marcado como tal, o "—" si la operación no estima por modelo.
export const tokenEstimateLabel = (operationType, item) => {
  if (item?.estimated_input_tokens != null) {
    return `${formatTokens(item.estimated_input_tokens)} / ${formatTokens(item.estimated_output_tokens ?? 0)}`;
  }
  const fallback = TOKEN_ESTIMATES_DEFAULT[operationType];
  return fallback ? `${formatTokens(fallback[0])} / ${formatTokens(fallback[1])} (default)` : '—';
};

// Espejo de `PAID_PLAN_OPERATIONS` en whisperbi-credits-engine/src/credits-engine/limit_resolver.py:
// operaciones exclusivas de planes de pago cuando su fila no dice otra cosa
// (requires_paid_plan). El bloqueo real lo hace credits-engine en `reserve`.
const PAID_PLAN_OPERATIONS_DEFAULT = ['DEEP_ANALYSIS', 'REPORT_GENERATION', 'DASHBOARD_REFRESH'];

export const requiresPaidPlan = (operationType, item) => (
  item?.requires_paid_plan != null ? Boolean(item.requires_paid_plan) : PAID_PLAN_OPERATIONS_DEFAULT.includes(operationType)
);

const emptyForm = {
  operation_type: '',
  base_credits: '0',
  token_multiplier: '1',
  infrastructure_multiplier: '1',
  minimum_credits: '',
  maximum_credits: '',
  estimated_input_tokens: '',
  estimated_output_tokens: '',
  enabled: true,
  requires_paid_plan: false
};

const BillingOperationPricingContent = () => {
  const [configured, setConfigured] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const items = await CreditsEngineService.listOperationPricing();
      const byType = {};
      items.forEach((item) => {
        byType[item.operation_type] = item;
      });
      setConfigured(byType);
    } catch (err) {
      setError(err.message || 'No fue posible obtener el pricing por operación.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (operationType) => {
    const item = configured[operationType];
    setFormValues({
      operation_type: operationType,
      base_credits: String(toNumber(item?.base_credits, 0)),
      token_multiplier: String(toNumber(item?.token_multiplier, 1)),
      infrastructure_multiplier: String(toNumber(item?.infrastructure_multiplier, 1)),
      minimum_credits: item?.minimum_credits != null ? String(toNumber(item.minimum_credits)) : '',
      maximum_credits: item?.maximum_credits != null ? String(toNumber(item.maximum_credits)) : '',
      estimated_input_tokens: item?.estimated_input_tokens != null ? String(toNumber(item.estimated_input_tokens)) : '',
      estimated_output_tokens: item?.estimated_output_tokens != null ? String(toNumber(item.estimated_output_tokens)) : '',
      enabled: item ? item.enabled !== false : true,
      requires_paid_plan: requiresPaidPlan(operationType, item)
    });
    setFormOpen(true);
  };

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    const payload = {
      operation_type: formValues.operation_type,
      base_credits: Number(formValues.base_credits || 0),
      token_multiplier: Number(formValues.token_multiplier || 1),
      infrastructure_multiplier: Number(formValues.infrastructure_multiplier || 1),
      enabled: formValues.enabled,
      requires_paid_plan: formValues.requires_paid_plan
    };
    if (formValues.minimum_credits !== '') {
      payload.minimum_credits = Number(formValues.minimum_credits);
    }
    if (formValues.maximum_credits !== '') {
      payload.maximum_credits = Number(formValues.maximum_credits);
    }
    if (formValues.estimated_input_tokens !== '') {
      payload.estimated_input_tokens = Number(formValues.estimated_input_tokens);
      payload.estimated_output_tokens = Number(formValues.estimated_output_tokens || 0);
    }
    try {
      setIsSaving(true);
      await CreditsEngineService.upsertOperationPricing(payload);
      setSnackbar({ open: true, message: 'Pricing de operación guardado correctamente.', severity: 'success' });
      setFormOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar el pricing.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer
      title="Precios por operación"
      description={
        'Piso de créditos estimado por tipo de operación (usado al reservar, antes de conocer el costo real por tokens). ' +
        'No incluye `model_multiplier` en esta vista — se administra directamente en la tabla si hace falta ajustar por modelo.'
      }
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      <Section title="Tipos de operación" subtitle="operation_type es un enum cerrado — solo edición, sin crear/borrar filas.">
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Tipo de operación</TableCell>
                  <TableCell sx={headSx} align="right">Créditos base</TableCell>
                  <TableCell sx={headSx} align="right">Multiplicador tokens</TableCell>
                  <TableCell sx={headSx} align="right">Multiplicador infra</TableCell>
                  <TableCell sx={headSx} align="right">Tokens estimados (entrada / salida)</TableCell>
                  <TableCell sx={headSx}>Estado</TableCell>
                  <TableCell sx={headSx}>Disponible en</TableCell>
                  <TableCell sx={headSx} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {OPERATION_TYPES.map((operationType) => {
                  const item = configured[operationType];
                  return (
                    <TableRow key={operationType} hover>
                      <TableCell sx={cellSx}>{operationType}</TableCell>
                      <TableCell sx={cellSx} align="right">{item ? toNumber(item.base_credits).toFixed(2) : '—'}</TableCell>
                      <TableCell sx={cellSx} align="right">{item ? toNumber(item.token_multiplier, 1).toFixed(2) : '—'}</TableCell>
                      <TableCell sx={cellSx} align="right">{item ? toNumber(item.infrastructure_multiplier, 1).toFixed(2) : '—'}</TableCell>
                      <TableCell sx={cellSx} align="right">{tokenEstimateLabel(operationType, item)}</TableCell>
                      <TableCell sx={cellSx}>
                        {item ? (
                          <Chip
                            size="small"
                            label={item.enabled !== false ? 'ACTIVO' : 'INACTIVO'}
                            sx={{
                              backgroundColor: item.enabled === false ? 'var(--wb-danger-16)' : 'var(--wb-x-bg-22c55e29, rgba(34,197,94,0.16))',
                              color: item.enabled === false ? 'var(--wb-danger)' : 'var(--wb-success)'
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: 'var(--wb-muted-60)', fontSize: '0.8rem' }}>Sin configurar</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {requiresPaidPlan(operationType, item) ? (
                          <Chip
                            size="small"
                            label="Solo planes de pago"
                            sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}
                          />
                        ) : (
                          <Typography sx={{ color: 'var(--wb-muted-60)', fontSize: '0.8rem' }}>Todos los planes</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx} align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(operationType)} sx={{ color: 'var(--wb-blue)' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Dialog open={formOpen} onClose={() => !isSaving && setFormOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 480 } } }}>
        <DialogTitle>Editar precio de operación: {formValues.operation_type}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Créditos base"
              value={formValues.base_credits}
              onChange={handleChange('base_credits')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-por-operacion" anchor="créditos-base" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Multiplicador por tokens"
              value={formValues.token_multiplier}
              onChange={handleChange('token_multiplier')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-por-operacion" anchor="multiplicador-por-tokens" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Multiplicador de infraestructura"
              value={formValues.infrastructure_multiplier}
              onChange={handleChange('infrastructure_multiplier')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-por-operacion" anchor="multiplicador-de-infraestructura" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Créditos mínimos (opcional)"
              value={formValues.minimum_credits}
              onChange={handleChange('minimum_credits')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-por-operacion" anchor="créditos-mínimos-opcional" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Créditos máximos (opcional)"
              value={formValues.maximum_credits}
              onChange={handleChange('maximum_credits')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-por-operacion" anchor="créditos-máximos-opcional" /></InputAdornment>
              ) }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Tokens de entrada estimados (opcional)"
                value={formValues.estimated_input_tokens}
                onChange={handleChange('estimated_input_tokens')}
                type="number"
                inputProps={{ step: '1', min: 0 }}
                placeholder={TOKEN_ESTIMATES_DEFAULT[formValues.operation_type] ? String(TOKEN_ESTIMATES_DEFAULT[formValues.operation_type][0]) : ''}
                fullWidth
                size="small"
                sx={darkTextFieldStyle}
              />
              <TextField
                label="Tokens de salida estimados (opcional)"
                value={formValues.estimated_output_tokens}
                onChange={handleChange('estimated_output_tokens')}
                type="number"
                inputProps={{ step: '1', min: 0 }}
                placeholder={TOKEN_ESTIMATES_DEFAULT[formValues.operation_type] ? String(TOKEN_ESTIMATES_DEFAULT[formValues.operation_type][1]) : ''}
                fullWidth
                size="small"
                sx={darkTextFieldStyle}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)', mt: -1 }}>
              Tokens típicos de una operación. La reserva previa se calcula con ellos al precio del modelo que va a
              responder (un Análisis Profundo con Opus reserva más que un chat con Haiku). Vacío = valor por defecto
              {TOKEN_ESTIMATES_DEFAULT[formValues.operation_type]
                ? ` (${tokenEstimateLabel(formValues.operation_type, null).replace(' (default)', '')}).`
                : '; esta operación no tiene uno y reserva sus créditos base.'}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.enabled}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, enabled: e.target.checked }))}
                  />
                }
                label="Habilitado"
              />
              <HelpIcon module="creditos" topic="precios-por-operacion" anchor="habilitado" />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.requires_paid_plan}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, requires_paid_plan: e.target.checked }))}
                />
              }
              label="Solo planes de pago (las cuentas gratuitas reciben un aviso para cambiar de plan)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={isSaving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : null}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const BillingOperationPricing = () => (
  <RequireBillingRole allow="root" title="Precios por operación">
    <BillingOperationPricingContent />
  </RequireBillingRole>
);

export default BillingOperationPricing;
